/**
 * Generate a fresh idempotency key for a single user-initiated action.
 *
 * Paired with the backend's `Idempotency-Key` header support: if the client
 * double-fires the same click (slow network + impatient user), both requests
 * carry the same key and the server replays the first response instead of
 * actually processing twice.
 *
 * Callers must generate a NEW key for each distinct user intent — reusing a
 * key across different submit clicks would silently swallow legitimate follow-up
 * submissions.
 */
export function createIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Fallback for older browsers — not cryptographically strong but fine for a
  // short-lived dedup key.
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 11)}-${Math.random().toString(36).slice(2, 11)}`;
}

/**
 * Format a backend date for display.
 *
 * Accepts both bare `YYYY-MM-DD` strings (date-only, no timezone — e.g.
 * `claim_lines[i].date_from`, `service_date_from`, `service_date`) and full
 * ISO datetime strings. Bare dates are anchored to the local calendar rather
 * than parsed as UTC midnight, so a 4/17 DOS doesn't render as 4/16 for users
 * west of UTC.
 */
export const formatDateOnly = (
  dateString: string | null | undefined,
  options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' },
  fallback = '—',
): string => {
  if (!dateString) return fallback;
  const dateOnlyMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateString);
  const date = dateOnlyMatch
    ? new Date(Number(dateOnlyMatch[1]), Number(dateOnlyMatch[2]) - 1, Number(dateOnlyMatch[3]))
    : new Date(dateString);
  if (Number.isNaN(date.getTime())) return fallback;
  return date.toLocaleDateString('en-US', options);
};

/**
 * Download a base64-encoded PDF as a file.
 */
export function downloadBase64Pdf(pdfData: string, filename: string) {
  const byteCharacters = atob(pdfData);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  const blob = new Blob([byteArray], { type: 'application/pdf' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
}
